import { useState, useRef, useCallback, useEffect } from "react";
import { getDefaultFormValues } from "../config/defaultValues";
import {
  loadDemoInput,
  saveDemoInput,
  clearDemoInput,
} from "../utils/demoStorage";
import { mergeDemoInput } from "../utils/mergeDemoInput";

// デモ（TOPページ埋め込み）の入力内容を localStorage に自動保存 / 復元する。
// 保存・読み込みの失敗は demoStorageWarning（"save" | "load" | null）で通知する。
export function useDemoDraft({ demo, methods, watchedValues, dirty, onRestore }) {
  const restoredRef = useRef(false);
  const [demoStorageWarning, setDemoStorageWarning] = useState(null);
  // 保存失敗の警告は1画面表示につき1回だけ
  const saveWarnedRef = useRef(false);
  // 読み込み失敗中（＝壊れたデータが残ったまま）は、保存失敗の警告で上書きしない
  const loadFailedRef = useRef(false);

  const dismissDemoStorageWarning = useCallback(() => {
    loadFailedRef.current = false;
    setDemoStorageWarning(null);
  }, []);

  // 現在の入力を即時保存する。成功で警告を消し、失敗時は1回だけ警告を出す。
  // Step移動・一時保存などの明示的な保存なので、dirty を見ずに常に現在値を書き込む。
  // （dirty はフォームの初期化時点との差分でしかなく、「入力→保存→全部削除」で
  //  現在値がその初期値と一致すると false に戻ってしまい、削除した内容が保存されずに残るため）
  const flushDemoDraft = useCallback(() => {
    if (!demo) return;
    if (saveDemoInput(methods.getValues())) {
      // 読み込み失敗の警告は「入力をリセット」または閉じるまで残す（9.4）。
      // 保存の成功だけで自動的に消すと、Step移動のたびに警告が消えてしまう
      if (!loadFailedRef.current) setDemoStorageWarning(null);
      return;
    }
    // 読み込み失敗の案内（壊れたデータのリセットを促す）を、保存失敗の案内で消さない
    if (loadFailedRef.current) return;
    if (!saveWarnedRef.current) {
      saveWarnedRef.current = true;
      setDemoStorageWarning("save");
    }
  }, [demo, methods]);

  // 初回だけ localStorage → フォームへ復元
  useEffect(() => {
    if (!demo || restoredRef.current) return;
    restoredRef.current = true;

    const { values, failed } = loadDemoInput();
    if (failed) {
      // 読み込み失敗時は復元せず、Step 1 のまま通知する
      loadFailedRef.current = true;
      setDemoStorageWarning("load");
    } else if (values) {
      // 項目のズレは復元時に寛容に扱う
      methods.reset(mergeDemoInput(values, getDefaultFormValues()));
      // 復元しただけなので、この内容を「まだ何も編集していない状態」として扱ってもらう
      onRestore?.();
    }
  }, [demo, methods, onRestore]);

  // 入力が変わったら 500ms 後に保存
  useEffect(() => {
    if (!demo || !dirty) return;
    const id = setTimeout(flushDemoDraft, 500);
    return () => clearTimeout(id);
  }, [demo, dirty, watchedValues, flushDemoDraft]);

  // 入力内容をすべて削除する
  const clearDemoForm = useCallback(() => {
    clearDemoInput();
    methods.reset(getDefaultFormValues());
    loadFailedRef.current = false;
    setDemoStorageWarning(null);
  }, [methods]);

  return {
    flushDemoDraft,
    clearDemoForm,
    demoStorageWarning,
    dismissDemoStorageWarning,
  };
}
